/**
 * Help Center: "booking" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'search-for-flights',
    title: 'How to search for flights',
    summary: 'Open Book or the search on Home, enter your route, dates, travelers and cabin, then pick your flights. Prices include taxes and fees by default.',
    keywords: ['find a flight', 'book a flight', 'one way', 'round trip', 'business class', 'first class', 'airfare', 'flight prices'],
    related: ['booking/cant-find-a-flight', 'booking/which-airlines', 'booking/checkout-page', 'flight-and-hotel/book-flight-and-hotel'],
    updated: '2026-10-05',
    body: `To search for flights, open [Book](/search) from the main menu, or use the search box on the **Home** page. Enter where you're flying from and to, your travel dates, how many people are traveling and your cabin, then run your search.

Searching doesn't cost anything or commit you to anything. You'll see your full trip total and your payment plan at checkout, before you pay.

## How do I search?

Here's what happens:

1. **Open the search.** Tap **Book** in the main menu, or start from the search box on **Home**.
2. **Choose Flights.** At the top of the search box, tap **Flights** for a flight on its own. If you'd like a hotel too, tap **Stays** instead to build a Flight + Hotel package.
3. **Enter your trip.** Choose one-way or round trip. Then add your origin, destination, travel dates and number of passengers, and pick your cabin: Economy, Premium Economy, Business or First.
4. **Pick your flights.** Run the search and choose a flight from your results. For a round trip, you pick your outbound flight first, then your return.
5. **Continue to checkout.** You'll choose your fare, add your travelers' details and choose how to pay. You'll see exactly what you'll pay before you book.

Your results come from real-time airline inventory, across hundreds of airlines.

## Is the price I see the full price?

Yes. Prices are shown all-in by default, with the **Grand total — taxes & fees included** toggle switched on. Each price already includes the airline's taxes and carrier fees, so it isn't a starting price that grows later.

If you'd rather compare base fares, you can switch the toggle off. Those prices are always labeled "fare", never "total".

Whichever way you set it, [your checkout page](/help/booking/checkout-page) shows your full trip total before you pay. That total includes our service fee and hold fee, and nothing is added to it later.

## Which dates can I choose?

The calendar starts {{PLAN_MIN_DAYS}} days from today. Every Defera trip departs at least {{PLAN_MIN_DAYS}} days after you book, so [a trip that leaves sooner](/help/booking/booking-close-to-departure) can't be booked, even if you'd pay for it in full.

## What else should I know while I browse?

- **Prices aren't held while you search.** Fares can change until you book. Once your booking is approved, your agreed-upon trip price, the price you agree to at checkout, is secured.
- **Some flights are flown by a partner airline.** Your results show these as "Operated by" another airline. This is called [a codeshare flight](/help/booking/codeshare-flights), and it's completely normal.
- **Defera Zero depends on the fare.** Defera Zero, our plan with nothing due at booking, is available only on refundable fares from {{ZERO_AIRLINES}}. If you don't see it as an option, that fare isn't eligible. You can book with Defera Installments instead: a deposit today, then weekly or bi-weekly payments.

## Can't find the flight you want?

Try searching again with nearby dates or times, which often bring up different routings. If you saw a flight on another site that isn't showing here, here's [why a flight you saw elsewhere might not be on Defera](/help/booking/cant-find-a-flight), and how we can help.`,
  },
  {
    slug: 'booking-close-to-departure',
    title: 'Can I book a trip that leaves soon?',
    summary: 'Every Defera trip departs at least {{PLAN_MIN_DAYS}} days after you book. The calendar starts there, so closer dates can’t be booked, even if you’d pay in full.',
    keywords: ['last minute', 'last-minute flight', 'short notice', 'flight this week', 'next week', 'earliest date', 'dates greyed out', 'soon'],
    related: ['getting-started/how-far-ahead-to-book', 'booking/search-for-flights', 'booking/choosing-a-deposit', 'getting-started/defera-zero'],
    updated: '2026-10-05',
    body: `Only if it leaves at least {{PLAN_MIN_DAYS}} days from now. Every Defera trip departs at least {{PLAN_MIN_DAYS}} days after you book, so a trip that leaves sooner can't be booked on Defera, even if you'd like to pay for it all at once.

We know that's disappointing when you need to travel soon. The upside is that the earliest date is easy to spot, and booking further ahead gives you more choice in how you pay.

## What will I see when I search?

- **The calendar starts {{PLAN_MIN_DAYS}} days out.** The first date you can pick is {{PLAN_MIN_DAYS}} days from today, and earlier dates can't be selected.
- **Checkout follows the same rule.** Search and checkout both start {{PLAN_MIN_DAYS}} days ahead, so a closer date can't be booked at a later step either.
- **Round trips work the same way.** Both flights need to be at least {{PLAN_MIN_DAYS}} days away. Your return always comes after your departure, so your departure date is the one to check.

For example, a trip that leaves {{PLAN_MIN_DAYS}} days from today can be booked, but a trip that leaves one day sooner can't.

## Can I pay in full to book a closer date?

No. The rule applies to every trip, however you pay, so paying for the whole trip at checkout doesn't change it.

## What if my dates are flexible?

Look at departures {{PLAN_MIN_DAYS}} or more days from today. Searching nearby dates and times can also bring up different routings, so it's worth trying a few.

## How can I keep what I pay at booking low?

Your [deposit options](/help/booking/choosing-a-deposit) depend on how far away your trip is, and they can be as low as 5% on trips more than 90 days away. So booking further ahead can mean paying less today.

On refundable fares from {{ZERO_AIRLINES}}, Defera Zero lets you book with nothing due at checkout.

## Already have a trip with us?

If you'd like to travel on different dates on the same route, see [Changing your travel dates or times](/help/changes-cancellations/change-dates-or-times).

## Need help with a trip that's coming up soon?

Chat with the [Defera Concierge](/support), our AI concierge, any time, or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7. For anything time-sensitive about a trip you've already booked, see [Getting urgent help before your flight](/help/contact/urgent-help-before-a-flight).`,
  },
  {
    slug: 'which-airlines',
    title: 'Which airlines can I book?',
    summary: 'Hundreds, from United, American and Delta to British Airways, Emirates and Lufthansa. A few features depend on the airline, and here’s which ones.',
    keywords: ['carriers', 'supported airlines', 'united', 'delta', 'american', 'southwest', 'international airlines', 'partner airlines'],
    related: ['getting-started/defera-zero', 'changes-cancellations/change-your-flight', 'booking/codeshare-flights', 'seats-and-bags/no-seat-map'],
    updated: '2026-10-05',
    body: `You can book flights on hundreds of airlines with Defera, including United, American, Delta, British Airways, Emirates, Lufthansa, Air France, Southwest, JetBlue, Virgin Atlantic, KLM, Air Canada, ANA, Qatar and Singapore.

Defera Installments, our standard plan, works on any fare from any airline we support: you pay a deposit today, then the rest in weekly or bi-weekly payments. A few extra features depend on the airline or the fare you choose, so it helps to know which ones before you book.

## Which features depend on the airline?

Here's how the features that vary compare:

| Feature | Where it's available |
|---|---|
| Defera Installments | Any fare on any airline we support |
| Defera Zero (nothing due at booking) | Refundable fares on {{ZERO_AIRLINES}} |
| Change Flight (new dates or times, in the app) | Fares other than Basic Economy on {{CHANGE_FLIGHT_AIRLINES}} |
| Live seat map | Typically American, United, British Airways and many international airlines. Not available on {{SEAT_MAP_UNSUPPORTED_AIRLINES}} |

The takeaway: you can book any airline we support on a payment plan. Where a feature isn't available, there's still a way to get what you need, as the next sections explain.

## What if I don't see Defera Zero?

[Defera Zero](/help/getting-started/defera-zero) is offered only on refundable fares from {{ZERO_AIRLINES}}. If you don't see it, the fare is non-refundable or on another airline, and you can book with Defera Installments instead. Flight + Hotel packages always use Defera Installments.

## What if my trip doesn't show Change Flight?

Every trip has a **Flight Options** menu, at the top right of the trip's **Flight Details** section. On airlines and fares where **Change Flight** isn't available, that menu shows **Request Flight Change** instead.

It opens a chat with the Defera Concierge, our AI concierge, with your request already written, so our team can help with new dates or times on the same route. Here's more on [how to change your flight](/help/changes-cancellations/change-your-flight).

## What if there's no live seat map?

Sometimes the live map isn't available, because of the airline, the route, or a fare that doesn't include advance seat selection, such as Basic Economy. In that case, **Purchase Seats** in **Flight Options** opens a chat with the Defera Concierge with your seat request already written.

Either way, seating your party together is the default on every booking.

## Why can't I find a particular airline?

It may be [a codeshare flight](/help/booking/codeshare-flights): one sold under one airline's name but flown by a partner, and shown as "Operated by" that partner. If you're looking for an airline on a route and don't see it, a partner may operate most or all of those flights, so they're listed under the partner.

## Can I add my frequent flyer number?

Yes. All major airline loyalty programs are supported, including United MileagePlus, American AAdvantage, Delta SkyMiles, British Airways Executive Club, Emirates Skywards and Lufthansa's Miles & More. Add your number with your traveler details at checkout, or save it with your travelers under **Passengers** on your [profile](/profile) so it fills in next time.

On a flight operated by a partner airline, the miles typically go to the program of the airline that sold the seat.`,
  },
  {
    slug: 'flight-plus-hotel-overview',
    title: 'Can I book a hotel with my flight?',
    summary: 'Yes. Tap Stays on Home or Book, search your flights, then pick a hotel and room. Your flight and hotel become one trip, paid with Defera Installments.',
    keywords: ['stays', 'vacation package', 'lodging', 'accommodation', 'package deal', 'flight and hotel', 'room'],
    related: ['flight-and-hotel/book-flight-and-hotel', 'flight-and-hotel/hotel-information', 'getting-started/defera-installments', 'booking/search-for-flights'],
    updated: '2026-10-05',
    body: `Yes. You can book a flight and a hotel together as a Flight + Hotel package. On **Home** or **Book**, tap **Stays** at the top of the search box and search for your flights as usual. After you pick them, you choose a hotel and a room in your destination city.

Your flight and hotel are checked out together as one Defera trip, with one payment plan. The hotel dates are matched to your flights for you, and the whole package, from choosing your room to your payment schedule, stays inside Defera.

## How does it work?

Here's what happens:

1. **Tap Stays.** On **Home** or **Book**, tap **Stays** in the toggle at the top of the search box.
2. **Search for your flights.** Enter your origin, destination, dates, passengers and cabin, the same way you'd search for a flight on its own.
3. **Pick your flights.** Choose your outbound flight, and your return if it's a round trip.
4. **Choose your hotel.** You'll land on the Stays page for your destination city. Your check-in date is set from when your outbound flight arrives, and your check-out date from when your return flight leaves. You can sort hotels by price or rating, search the list, and switch between a list and a map.
5. **Choose your room.** Each hotel shows its room rates, so you can pick a specific room.
6. **Check out once.** Your flight and hotel are booked together as one trip, with one deposit and one payment schedule.

[Booking a flight and hotel together](/help/flight-and-hotel/book-flight-and-hotel) walks through each screen.

## How do I pay for a package?

Flight + Hotel packages are paid with Defera Installments: a deposit at booking, then automatic weekly or bi-weekly payments that cover the combined flight and hotel total. Deposits, price protection, no credit check and no interest all work the same way as for a flight on its own.

- **Defera Zero isn't available on packages.** If you'd like nothing due at booking, a package isn't eligible for Defera Zero, so you'd pay a deposit on the combined total.
- **Miles can help.** You can apply Defera Miles at checkout on a package. A package can't be booked with Miles alone, though, so part of it always stays on your payment plan.

## Can I book a hotel on its own?

No. A Flight + Hotel package always includes a flight, and there's no way to book a hotel by itself.

## Can I add a hotel to a flight I've already booked?

Not to the same trip. A hotel can't be added to a flight you've already booked, because packages are built together at booking. Searching with **Stays** starts a new, separate trip, with its own flight and its own payment plan.

If you'd like a hotel for a trip you've already booked, chat with the [Defera Concierge](/support), our AI concierge, and it can send a request to our team.

## Will I pay anything at the hotel?

Possibly. A package can carry fees the hotel collects when you check in. These are labeled on your trip, and they're the one exception to our all-in prices.`,
  },
  {
    slug: 'cant-find-a-flight',
    title: 'Why can’t I find a flight I saw somewhere else?',
    summary: 'What’s bookable on Defera can differ from other sites, and it changes daily. Try nearby dates or times, or ask us to look at a specific flight.',
    keywords: ['google flights', 'airline website', 'missing flight', 'not showing', 'cheaper elsewhere', 'different price', 'flight not available', 'itinerary'],
    related: ['booking/search-for-flights', 'booking/codeshare-flights', 'booking/only-connecting-flights', 'contact/how-requests-work'],
    updated: '2026-10-23',
    body: `What's bookable on Defera can differ from what other sites, or the airline's own website, show, and availability shifts from day to day. So a flight you saw elsewhere may not be bookable on Defera at that moment.

That doesn't leave you stuck. Searching nearby dates or times often brings up different routings, and if one specific flight really matters to you, our team can take a look.

## Why would a flight show up elsewhere but not here?

Every site lists the flights it can sell, and those lists aren't always the same. The flights you can book on Defera come from our airline partners in real time, and what's bookable here can differ from what other sales channels list. On top of that, availability changes from day to day.

So a flight you saw on Google or an airline's website might not be bookable on Defera today, even though our results are live. Not every itinerary sold elsewhere is bookable here at a given moment.

## What can I try?

1. **Search again with nearby dates or times.** On **Book**, try a day earlier or later, or a different time of day. Nearby dates and times often bring up different routings.
2. **Check the "Operated by" line.** If you're looking for a particular airline, it may be listed under a partner. A flight sold under one airline's name is often flown by a partner airline, so it can appear under the airline that operates it. Here's [how codeshare flights work](/help/booking/codeshare-flights).

## Can you look into a specific flight for me?

Yes. If a particular itinerary matters to you, chat with the [Defera Concierge](/support), our AI concierge, and share:

- the airline and flight numbers
- your travel dates and times
- how many people are traveling

The concierge can send a request to our team, who can take a look and come back to you with a real answer.

## Comparing prices with another site?

Defera shows prices all-in by default, so fares already include the airline's taxes and carrier fees. At checkout, [your price breakdown](/help/getting-started/what-you-pay) itemizes your whole trip total, including our service fee and hold fee, and there are no fees beyond what it shows.

## How else can I reach you?

- Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7.
- Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'only-connecting-flights',
    title: 'Why am I only seeing flights with stops?',
    summary: 'Your results show what’s bookable on Defera right now, which changes daily. Try nearby dates or times, or ask us to look at a nonstop you’ve seen.',
    keywords: ['nonstop', 'direct flight', 'layover', 'connecting flight', 'one stop', 'faster route', 'no direct flights'],
    related: ['booking/cant-find-a-flight', 'booking/free-flight-upgrade-question', 'confirmation-and-travel/layovers-and-connections', 'booking/search-for-flights'],
    updated: '2026-10-05',
    body: `Your results show the flights that are bookable on Defera for your search at that moment, and sometimes that means only flights with a stop. What's bookable here can differ from what other sites list, and availability shifts from day to day.

If a nonstop matters to you, a few changes to your search can help. And if you've seen a specific nonstop somewhere else, we can look into it for you.

## Nonstop or connecting: what's the difference?

A nonstop flight takes you straight to your destination. A flight with a stop lands at least once along the way, usually so you can change planes. The time you spend there between flights is your layover.

## Why am I only seeing flights with stops?

The flights you can book on Defera come from our airline partners in real time. What's bookable here can differ from what other sites or the airline's own website list, and availability changes day to day. So for some routes and dates, every bookable option may include a stop.

## What can I try?

- **Nearby dates.** A day or two earlier or later can bring up different routings.
- **Different times.** Changing the time of day can bring up different routings too.

Search again on [Book](/search) with your new dates or times.

## I saw a nonstop somewhere else. Can you check it?

Yes. Chat with the [Defera Concierge](/support), our AI concierge, and share the airline, flight number, dates and times of the flight you found. The concierge can send a request to our team, who can look at that specific flight and come back to you with a real answer.

## Could my flight get faster after I book?

It could, if you say yes to one question at checkout. Every booking answers the Free Flight Upgrade question: if our team spots a faster route to your destination, with a shorter total trip time or a shorter layover, may we move you to it?

If you say yes, any move keeps your cabin, your fare and your travel dates, with a departure near your original time. It never costs you extra, and we email you if we make a change. It's permission to look, not a plan to move you, and most bookings are never changed. Here's [what saying yes to the Free Flight Upgrade means](/help/booking/free-flight-upgrade-question).

## Questions about a connection on your trip?

For what to expect on a trip with a connection, see [Layovers and connecting flights](/help/confirmation-and-travel/layovers-and-connections), or ask the Defera Concierge.`,
  },
  {
    slug: 'codeshare-flights',
    title: 'What is a codeshare flight?',
    summary: 'It’s a flight sold by one airline but flown by a partner, shown as “Operated by X as Y”. It’s completely normal. Here’s which airline handles what.',
    keywords: ['operated by', 'partner airline', 'operating airline', 'marketing airline', 'different airline', 'wrong airline', 'check-in desk'],
    related: ['booking/which-airlines', 'confirmation-and-travel/airline-app-shows-nothing', 'confirmation-and-travel/check-in', 'seats-and-bags/bags-included'],
    updated: '2026-10-05',
    body: `A codeshare flight is a flight sold under one airline's name but flown by a partner airline. In your search results and itinerary, it shows as "Operated by X as Y": X is the airline that flies the plane, and Y is the airline that sold the seat.

This is completely normal. Airlines share flights with their partners all the time, and it doesn't mean anything is wrong with your booking.

## How do I read "Operated by"?

Take a flight shown as "Operated by X as Y":

- **X is the operating airline.** It flies the plane, its crew works the flight, and you check in at its desk.
- **Y is the airline that sold the seat.** Your flight number is Y's, and Y's loyalty program typically earns the miles.

For example, Alaska operates most flights between Los Angeles (LAX) and Honolulu (HNL) on Hawaiian Airlines' behalf. So those flights appear as "Operated by Alaska as Hawaiian Airlines": Alaska flies the plane, and the seat is sold under Hawaiian's name.

## Which airline handles what?

This table shows which airline looks after each part of your flight:

| What | Which airline |
|---|---|
| The aircraft and crew | The operating airline (X) |
| The check-in desk | The operating airline (X) |
| Baggage rules and seat map | Usually the operating airline (X) |
| The flight number you booked | The airline that sold the seat (Y) |
| The loyalty program that earns your miles | Typically the airline that sold the seat (Y) |

In short, the airline that flies the plane looks after you at the airport and on board. The airline that sold the seat gives you your flight number, and usually your miles.

## What do I do at the airport?

When you [check in for your flight](/help/confirmation-and-travel/check-in), go to the operating airline, the one shown after "Operated by". Its baggage rules usually apply too.

## How do I find my booking on the airline's website?

You'll look up your trip with your airline confirmation number, which is released once your trip is paid in full. If you can't find your booking on one airline's website or app, try the airline that operates your flight. That's usually where it lives.

If your confirmation number has a slash in it, there's one confirmation per airline, in the order you fly. The first code is for the first airline you fly, and the next is for the airline that operates your following flight or your return. Use each code with its own airline. If your trip still isn't showing, see [Why doesn't the airline's app show my trip yet?](/help/confirmation-and-travel/airline-app-shows-nothing)

## Looking for a particular airline?

An airline you're looking for may not appear on a route because a partner operates most or all of those flights, so they're listed under the partner. Check the "Operated by" line in your results, or try our tips for [finding a flight you saw somewhere else](/help/booking/cant-find-a-flight).`,
  },
  {
    slug: 'traveler-information',
    title: 'What traveler information do I need?',
    summary: 'Each traveler’s full legal name as it appears on their ID, date of birth and gender, plus contact info. International trips also need passport details.',
    keywords: ['passport', 'date of birth', 'legal name', 'known traveler number', 'frequent flyer', 'passenger details', 'id', 'gender'],
    related: ['booking/booking-for-others', 'account/saved-passengers', 'confirmation-and-travel/travel-documents', 'changes-cancellations/correct-traveler-details'],
    updated: '2026-10-05',
    body: `To book, you'll need each traveler's full legal name, exactly as it appears on their government ID, plus their date of birth and gender. At least one traveler on the booking also needs contact info. International flights need each traveler's passport number, passport expiration date and nationality too.

It helps to have these details ready before you start checkout. Taking a moment to check each name against the ID that traveler will use saves trouble later.

## What do I need for each traveler?

Here's what's needed, and when:

| Information | When it's needed |
|---|---|
| Full legal name, as it appears on government ID | Every traveler |
| Date of birth | Every traveler |
| Gender | Every traveler |
| Contact info | At least one traveler on the booking |
| Passport number, expiration date and nationality | International flights |
| Frequent flyer numbers | Optional |
| Known Traveler Number (for TSA PreCheck or Global Entry) | Optional |
| Special requests, such as meals or wheelchair assistance | Optional |

In short: a name, date of birth and gender for everyone, contact info for the booking, and passport details for international trips. Everything else is optional.

## Why does the name have to match the ID?

Enter each name exactly as it appears on the government ID the traveler will use, such as their passport or driver's license. That's the name the airline and airport security check against.

## What if I spot a mistake after booking?

Chat with the [Defera Concierge](/support), our AI concierge, and include your booking reference (the code at the top of your booking confirmation email), the details as booked and the correct details as they appear on the traveler's ID. The concierge can send a request to our team. The request shows on your trip page, and our team's reply arrives there and by email.

## What about international trips?

Every traveler on an international itinerary, including children and infants, needs [valid travel documents](/help/confirmation-and-travel/travel-documents), and we need passport details to book an international flight.

Entry rules, such as visas, vaccinations and how long a passport must be valid, are set by governments and airlines, and they can change. Check them for your destination with official sources: the US State Department, the CDC, your destination's embassy and your airline.

## What if a traveler needs wheelchair assistance?

Add it as a special request for that traveler. When a special request mentions a wheelchair, checkout asks the airline's four assistance questions for that traveler, each a yes or no. All four need an answer before checkout continues, because the airline can't arrange assistance without them.

After booking, you can change the answers at any time while the trip is active. Open **Trips**, tap your trip, then tap **Passengers**, and you'll find them in the "Wheelchair assistance" box under that traveler. Our team is notified of every change. Here's more on [requesting wheelchair assistance](/help/confirmation-and-travel/wheelchair-assistance) for your trip.

## Can I save travelers for next time?

Yes. Save the people you travel with under **Passengers** on your [profile](/profile), with their full legal name, date of birth, gender, contact info, passport, frequent flyer numbers and Known Traveler Number. Future bookings fill them in from a dropdown, so there's less to type.

## Where will I see these details after I book?

Your booking confirmation email lists every traveler detail entered at checkout. Passport, ID and Known Traveler numbers show only their last 4 digits there. You'll find the full numbers on your trip's Flight Details screen.`,
  },
  {
    slug: 'booking-for-others',
    title: 'Booking for other travelers',
    summary: 'You can book for family, friends or a group, whether or not you’re going too. Enter each person’s full legal name exactly as it appears on their ID.',
    keywords: ['family booking', 'group booking', 'multiple passengers', 'book for someone else', 'travel companion', 'kids', 'saved travelers', 'passengers'],
    related: ['booking/traveler-information', 'account/saved-passengers', 'seats-and-bags/sitting-together', 'changes-cancellations/correct-traveler-details'],
    updated: '2026-10-05',
    body: `Yes, you can book for other people, whether they're traveling with you or on their own, and family and group bookings are welcome. At checkout, you add [each traveler's details](/help/booking/traveler-information): their full legal name exactly as it appears on their government ID, their date of birth and gender. At least one traveler on the booking also needs contact info.

The trip lives in your Defera account, under **Trips**, and you can share the details with your travelers whenever you like. If you have Defera Credit, it can go toward trips you book for someone else, too.

## How do I add other travelers?

Here's what happens:

1. **Set the number of passengers.** When you search on **Book**, enter how many people are traveling.
2. **Add each traveler's details at checkout.** Enter their full legal name as it appears on their government ID, their date of birth and gender, plus contact info for at least one traveler.
3. **Add passport details for international flights.** For each traveler, enter their nationality, the country that issued their passport, the passport number and its expiration date. Checkout requires these before payment on international trips. Everyone on an international trip, including children and infants, needs valid travel documents.
4. **Add any extras.** Frequent flyer numbers, a Known Traveler Number and special requests, such as meals or wheelchair assistance, are optional.

## Can I save my travelers for next time?

Yes, and it makes every booking after this one quicker. Open your profile from the avatar menu, go to **Passengers** and tap **Add** to save someone new. Future bookings fill in their details from a dropdown, and you can edit or delete a saved traveler any time. Here's more on [saving travelers to your profile](/help/account/saved-passengers).

## Will we be seated together?

Seating your party together is the default on every booking. On fares with complimentary seat selection, you can pick seats together for free. Where there's no live seat map, our team arranges seats for everyone on the booking together.

If some of your group are on a different Defera booking, you can tell us at checkout. When the Defera Concierge asks whether you're traveling with others on this flight, answer **Yes**, then choose **Another Defera booking**. As that option says, "We can look for it and seat you together."

## How do I share the trip details with my travelers?

We can only send trip documents to the email on your account, so the easiest way to share is your Trip Statement. Open **Trips**, tap the trip, then tap the download icon in its header row. You'll get a PDF with the itinerary and passenger names, along with the trip's pricing and payment plan, and you can forward it to anyone you like.

## What if a name or date of birth is wrong?

Check every name carefully before you book. If a name or date of birth turns out to be wrong, chat with the [Defera Concierge](/support), our AI concierge, and include:

- your booking reference, the code at the top of your booking confirmation email
- the details as they were booked
- the correct details, as they appear on the traveler's ID

The concierge can send a request to our team. The request shows on your trip page, and our team's reply arrives there and by email.

## Traveling with an infant?

If you have a question about how checkout handles a particular traveler, such as a lap infant, ask the Defera Concierge before you book. It can send a request to our team, who can look at your checkout and give you a real answer.`,
  },
  {
    slug: 'checkout-page',
    title: 'What you’ll see at checkout',
    summary: 'Checkout shows what’s due today, your payment schedule and your full trip total before you pay. Here’s what each part of the page is for.',
    keywords: ['review and book', 'price breakdown', 'due today', 'trip total', 'service fee', 'hold fee', 'acknowledgment', 'email me this plan'],
    related: ['getting-started/what-you-pay', 'booking/using-credit-at-checkout', 'rewards/using-miles-at-checkout', 'travel-insurance/add-insurance-at-checkout'],
    updated: '2026-10-05',
    body: `Your checkout page, called Review & Book, shows exactly what you'll pay before you commit: what's due today, your payment schedule and your trip total, with a full **Price Breakdown**. Your trip total already includes our service fee and hold fee, so nothing extra is added later, and there's no interest.

It's also where you add your travelers, answer a few quick questions and choose how to pay. Nothing is charged until you complete your booking, so take your time and look everything over.

## What happens at checkout?

Here's what happens:

1. **You choose your seats, on supported airlines.** Right after you choose your fare, a [seat selection step](/help/seats-and-bags/seats-at-checkout) shows the airline's real cabin layout. Seats you pick are added to your trip total, so they can be paid over time with the rest of your trip.
2. **You add your travelers.** Each name must match the traveler's government ID, and international flights need passport details too.
3. **You answer a few quick questions.** The Defera Concierge asks about your trip, and every booking answers the Free Flight Upgrade question. Both are explained below.
4. **You review your price and choose your plan.** You'll see what's due today, your payments and your trip total. You choose your plan (Defera Installments, or Defera Zero when your fare is eligible), your deposit if your plan has one, and whether to pay weekly or bi-weekly.
5. **You apply any credits or Miles.** Defera Credit applies automatically, and Miles and airline credits are there if you want to use them.
6. **You add your card.** It's saved for your later payments.
7. **You agree and book.** One checkbox covers the acknowledgments, and then you complete your booking.

## What does the price section show?

Checkout leads with your plan:

- **Due today.** The headline is what you pay now, such as your deposit.
- **Your payments.** How many weekly or bi-weekly payments follow, the amount of each, and the date of the last one.
- **Your trip total.** The full price of your trip, including taxes and fees.

For example, if it shows 8 weekly payments of $50, last on Nov 4, you'll make eight more payments of $50, one a week, with the last on November 4.

Right above the total, a short strip sums up four promises: "No credit check", "0% interest", "Price protected from today" and "Defera Credit on select cancellations".

## What's in the Price Breakdown?

The [Price Breakdown](/help/getting-started/what-you-pay) is open by default, and you can collapse it and open it again. It lists:

- your base fare, after any credits you've applied
- the service fee, which covers dedicated support throughout your payment plan
- any seats you chose, shown as "Free" when they're included
- your subtotal before the hold fee
- the hold fee, which covers the cost of letting you pay over time
- your trip total

Each fee has a small info icon with a one-line explanation. Your deposit and payments are calculated on the subtotal before the hold fee, and the hold fee is included in your trip total. There are no fees beyond what the breakdown shows.

Flight + Hotel packages are the one exception: some hotels collect fees at check-in, and those are labeled on your trip.

## Why does checkout ask these questions?

### A few quick questions about your trip

Once your traveler details are in, a card from the Defera Concierge asks [what your trip is for and whether you're traveling with others](/help/booking/concierge-travel-questions). As the card puts it, "Your answers help your Concierge tailor your travel experience and look for ways to make this trip even better."

### The Free Flight Upgrade question

Above the total, every booking answers whether we may [move you to a faster route](/help/booking/free-flight-upgrade-question), with a shorter total trip time or a shorter layover, if our team spots one. Any move keeps your cabin, fare and travel dates, and "No" is a perfectly normal answer.

### Wheelchair assistance

If a traveler's special request mentions a wheelchair, you'll answer the airline's four [wheelchair assistance questions](/help/confirmation-and-travel/wheelchair-assistance) for that traveler. All four are needed, because the airline can't arrange assistance without them.

## How do credits and Miles work at checkout?

- **Defera Credit** (credit on your Defera account) applies automatically, to your deposit first. A green "Defera Credit applied" panel shows what's due today after your credit, and you can choose to use less.
- **Defera Miles** appear like a payment method, right above where you add your card. Nothing comes off unless you choose to use them. If your Miles cover the whole trip, checkout may offer to book it with Miles alone.
- **Travel Credits & Gift Cards** is where you apply an airline travel credit or eCredit, a certificate or voucher, or an airline gift card. The amount comes off your fare right away, and each one shows "Pending verification" until our team confirms it with the airline.

[Using Defera Credit at checkout](/help/booking/using-credit-at-checkout) covers credit in detail.

## Why does checkout ask for my card?

Unless you book with Miles alone, checkout asks for a debit or credit card. On a payment plan, it becomes your card on file: the card your later payments are charged to, automatically. That's why checkout asks for it even when little or nothing is due today.

The "Due today" line shows exactly what your card will be charged now. For example, with Defera Zero, or when Defera Credit covers your whole deposit, it's $0. Prepaid cards, including gift and stored-value cards, aren't accepted on payment plans.

## What's on the page before I pay?

- **A short FAQ.** Under your payment schedule, six common questions are answered on the spot: what you pay today and when the rest is due, whether there's a credit check or interest, what the service fee and hold fee are, what happens if you cancel or change, whether you can add seats or bags later, and when your confirmation number arrives.
- **One checkbox.** You agree to the acknowledgments with one checkbox. You're still agreeing to all five, and their full texts are one tap away under **Read the full terms**.

## Not ready to book yet?

Tap **Email me this plan**, under the pay button. It emails you the figures on screen, including what's due today, your payments and your trip total, with a link back to the same checkout.

Nothing is charged, and the email doesn't hold the price: fares can change until you book. If you leave at this step, you may also get one or two reminder emails with the same figures, about an hour later and the next day. You can unsubscribe from "Trip reminders" in any of them.

## Still have a question?

If something on your checkout page isn't covered here, chat with the [Defera Concierge](/support), our AI concierge, before you book. It can send a request to our team, who can look at your checkout and give you a real answer.`,
  },
  {
    slug: 'concierge-travel-questions',
    title: 'The travel questions at checkout',
    summary: 'Before you book, we ask what your trip is for, whether you’re traveling with others and a few other quick questions. Here’s what each one means.',
    keywords: ['purpose of travel', 'traveling with others', 'companions', 'booking reference', 'other booking', 'faster route', 'wheelchair', 'required question', 'trip details', 'assistance'],
    related: ['booking/free-flight-upgrade-question', 'confirmation-and-travel/wheelchair-assistance', 'booking/checkout-page', 'booking/traveler-information'],
    updated: '2026-10-08',
    body: `Before you book, checkout asks a few short questions about your trip. A card from the Defera Concierge asks what your trip is for and whether you're traveling with others. Every booking also answers the Free Flight Upgrade question. And if a traveler's special request mentions a wheelchair, you'll answer the airline's four assistance questions for them.

Each one only takes a moment. Your booking can't be completed until they're all answered, so here's what each question means and why we ask it.

## What does the Defera Concierge ask?

Once your traveler details are in, a card from the Defera Concierge appears, titled "A few quick questions about your trip". Nothing is pre-selected, so you choose each answer yourself.

Here's what it asks:

1. **What's the trip for?** The card asks "What is the purpose of your travel?" Choose **Vacation**, **Business** or **Event**.
2. **Is anyone else coming?** It asks "Will you be traveling with others on this flight?" Choose **Yes** or **No**. If you're booking for more than one traveler, the question adds "aside from passengers on this reservation", so it's only asking about people who aren't on this booking.
3. **How did they book?** This appears only if you answer **Yes**. Choose **Another Defera booking** ("We can look for it and seat you together") or **Booked elsewhere** ("With the airline or another site").

## Can I add the other trip's booking reference?

Yes, if you'd like. When you choose **Another Defera booking**, an optional field asks for the **booking reference of the other trip**. It's on that trip's confirmation email and in [Trips](/trips), and it helps our team find the other booking right away. If that trip is booked on someone else's account, our team checks with both of you before seating you together.

You can leave it blank. If the other trip isn't booked yet, book this one first, then enter this trip's reference when you book the other one.

## Why do you ask?

The card explains it in one line: "Your answers help your Concierge tailor your travel experience and look for ways to make this trip even better."

If your companions are on another Defera booking, that option says what it's for: "We can look for it and seat you together."

## What's the Free Flight Upgrade question?

Every booking also answers [the Free Flight Upgrade question](/help/booking/free-flight-upgrade-question), which is required: if our team spots a faster route to your destination, with a shorter total trip time or a shorter layover, may we move you to it? If you say yes, you also choose whether a move can be to any airline or only to the airline you booked.

"No" is a perfectly normal answer and has no downside. A yes keeps your cabin, fare and travel dates, never costs you extra, and isn't a promise that your flight will change.

## What are the wheelchair assistance questions?

If a traveler's special request mentions a wheelchair, checkout asks the airline's four [wheelchair assistance](/help/confirmation-and-travel/wheelchair-assistance) questions for that traveler. Each is a yes or no:

- Can they walk from the ticket counter to the aircraft door?
- Do they need help on stairs while boarding?
- Do they need to be lifted and transferred onto their seat?
- Are they traveling with their own wheelchair?

All four need an answer before checkout continues, because the airline can't arrange assistance without them. After booking, you can change the answers at any time while the trip is active, under **Passengers** on your trip, and our team is notified of every change.

## Not sure how to answer?

If checkout asks something that isn't covered here, or you're not sure how to answer, chat with the [Defera Concierge](/support), our AI concierge, before you book. It can send a request to our team, who can look at your checkout and give you a real answer.

For the traveler details checkout asks for, such as names and dates of birth, see [What traveler information do I need?](/help/booking/traveler-information)`,
  },
  {
    slug: 'free-flight-upgrade-question',
    title: 'The Free Flight Upgrade question at checkout',
    summary: 'It asks if we may move you to a faster route when our team spots one, with the same cabin and dates, at no extra cost. It isn’t a cabin upgrade.',
    keywords: ['faster route', 'shorter layover', 'reroute', 'move my flight', 'cabin upgrade', 'business class', 'permission'],
    related: ['seats-and-bags/upgrades', 'booking/checkout-page', 'changes-cancellations/change-your-flight', 'booking/concierge-travel-questions'],
    updated: '2026-10-05',
    body: `At checkout, every booking answers one required question: if our team spots a faster route to your destination, with a shorter total trip time or a shorter layover, may we move you to it? Saying yes gives us permission to look. It isn't a plan to move you.

Despite its name, it isn't a cabin upgrade. The "upgrade" is a better route: you'd keep your cabin, your fare and your travel dates, and it never costs you anything extra.

## How does the question work?

- **Where you'll see it.** It's on your checkout page, above the total, and every booking needs an answer.
- **Yes or no.** If you say yes, you also choose whether a move can be to any airline or only to the airline you booked.
- **No downside to "No".** "No" is a perfectly normal answer. It's a preference, not a legal agreement.

## What does a yes mean?

If you say yes, here's what we promise:

- **Same cabin.** Economy stays economy. The improvement is a shorter trip, not a better class of service.
- **Same travel dates,** with a departure near your original time.
- **No extra cost, ever.** If a faster option costs more, we cover the difference, or we don't move you.
- **Only a real improvement.** We'd only move you for a shorter total trip time or a shorter layover, never to a worse itinerary.
- **You'll hear from us.** If we make a change, we email you. You're never moved silently.
- **Nothing else changes.** A yes doesn't waive anything, and it doesn't affect your fare rules, refundability, seats you've paid for or your payment plan. If a move would ever affect a seat assignment you paid for, we'd sort that out with you first.

For example, say your trip has a long layover, and our team spots a flight that leaves around the same time on the same day and gets you there sooner. With a yes on file, we could move you to it, in the same cabin and at no cost, and we'd email you about the change.

## Will my flight change?

Not necessarily. Most bookings are never changed, and it may never come up for your trip. If you'd like different dates or times yourself, that's a separate thing: here's [how to change your flight](/help/changes-cancellations/change-your-flight).

## Can I change my answer?

Yes, either way. Chat with the [Defera Concierge](/support), our AI concierge, and it can send the change to our team. Your answer is a preference on your booking, not a contract term.

## How is this different from other upgrades?

"Upgrade" shows up in three places at Defera, and they're different things:

| Upgrade | What it is | How it happens |
|---|---|---|
| Free Flight Upgrade | A faster route, in the same cabin | You say yes at checkout |
| Complimentary cabin upgrade | A better cabin, as a thank-you for booking with us | We arrange it when we can. There's nothing to request |
| Cabin Upgrade | A better cabin, using your Defera Miles | You ask from your trip page, from {{MILES_UPGRADE_TILE}} Miles |

The takeaway: only the Free Flight Upgrade is about your route. The other two are about your cabin.

A complimentary cabin upgrade isn't guaranteed and isn't on every trip: it's subject to availability, and eligibility is assessed per booking. If one is arranged, you'll see a "Complimentary Upgrade" card on your trip page and get a "You've been upgraded" email, and it shows on your boarding pass at check-in.

**Cabin Upgrade** sits under "Redeem on this trip" on your trip page, and tapping it opens a chat with your request already written. [How upgrades work on Defera](/help/seats-and-bags/upgrades) covers all three.`,
  },
  {
    slug: 'choosing-a-deposit',
    title: 'Choosing your deposit',
    summary: 'Your deposit is what you pay today on Defera Installments. Your options depend on how far away your trip is: as low as 5% on trips more than 90 days away.',
    keywords: ['down payment', 'due today', 'minimum deposit', 'upfront payment', 'pay at booking', 'how much upfront', '5%'],
    related: ['getting-started/how-far-ahead-to-book', 'getting-started/installments-vs-zero', 'booking/checkout-page', 'payments/how-installments-work'],
    updated: '2026-10-05',
    body: `Your deposit is the amount you pay today when you book with Defera Installments. The rest of your trip total is then split into automatic weekly or bi-weekly payments. Your deposit options depend on how far away your trip is, and you'll see them at checkout. They can be as low as 5% on trips more than 90 days away.

Your deposit also does an important job: once your booking is approved, your agreed-upon trip price, the price you agree to at checkout, is secured.

## How do deposit options work?

- **How far away your trip is decides your options.** The further away your departure, the lower the deposits you can choose from. On trips more than 90 days away, they start as low as 5%.
- **You choose at checkout.** In your payment schedule, you pick your deposit and whether to pay weekly or bi-weekly. Checkout then shows what's due today, how many payments follow, the amount of each and the date of your last one.
- **It's calculated before the hold fee.** Your deposit is calculated on your subtotal before the hold fee. The hold fee, which covers the cost of letting you pay over time, is included in your trip total.
- **Trips depart at least {{PLAN_MIN_DAYS}} days out.** [Closer dates can't be booked](/help/booking/booking-close-to-departure) at all.

For example, say your trip is more than 90 days away and your subtotal before the hold fee is $1,000. A 5% deposit would be $50, due today. The rest of your trip total, including the hold fee, is then split across your payments.

## Should I choose a smaller or larger deposit?

That's up to you and your budget. A smaller deposit means paying less today, with a little more in each payment after. A larger deposit means paying more today, with smaller payments after. There's no interest, so your trip total is the same either way.

## What does my deposit secure?

Once your booking is approved, your agreed-upon trip price is secured. It's [protected from future airfare increases](/help/getting-started/price-protection) as long as you keep up with your payment plan, which we call being in good standing.

Your airline confirmation number comes later, once your trip is paid in full.

## Can I pay less today?

- **Defera Zero.** Nothing is due at booking on [Defera Zero](/help/getting-started/defera-zero), our 0%-down plan. It's available only on refundable fares from {{ZERO_AIRLINES}}, and not on Flight + Hotel packages.
- **Defera Credit.** Any credit on your account applies automatically at checkout, to your deposit first.
- **Defera Miles.** When you apply Miles at checkout, the price drops before your payment plan is built, so your deposit and every payment shrink.

## Can I pay more later?

Yes. You can pay ahead, make an extra payment or pay off your trip early at any time from **Make a Payment** on your trip, with no penalty. Paying off sooner gets your confirmation number released sooner.

## What if I change my mind?

If you [cancel within 24 hours of booking](/help/changes-cancellations/cancel-within-24-hours), your deposit is refunded in full to the card you paid with, automatically, with no questions asked. Most of the time, the charge drops off your statement within a day or so. If it had already posted, the money returns to your card within a few business days.`,
  },
  {
    slug: 'using-credit-at-checkout',
    title: 'Using Defera Credit at checkout',
    summary: 'Your credit applies automatically, to your deposit first. Checkout still asks for a card for later payments, and charges it $0 today if credit covers what’s due.',
    keywords: ['apply credit', 'credit balance', 'card required', 'due today', 'use less credit', 'save credit for later', 'deposit'],
    related: ['changes-cancellations/what-is-defera-credit', 'changes-cancellations/where-is-my-credit', 'changes-cancellations/use-defera-credit', 'booking/checkout-page'],
    updated: '2026-10-23',
    body: `Your Defera Credit applies automatically when you book a new trip. There's no code to enter and nothing to switch on. It goes to your deposit first, then to your upcoming payments, and a green "Defera Credit applied" panel on your checkout page shows exactly what's due today after your credit.

Checkout will still ask for a card, even if your credit covers everything due today. That's normal, and it isn't a request for payment. Here's how it all fits together.

## What is Defera Credit?

Defera Credit is credit on your Defera account that you can put toward a future trip with us. It never expires, and you can use it on trips you book for someone else, too. If you have any, the Defera Credits card on your profile shows your Available credit and its history.

## How does my credit apply?

Here's what happens at checkout:

1. **Your full balance applies by default.** You don't need to do anything to use it.
2. **It covers your deposit first.** The "Defera Credit applied" panel shows what's left to pay today.
3. **Anything left over goes to your payments.** Credit beyond your deposit shrinks your earliest payments.
4. **Anything more than the trip needs stays on your account.** You can use it for future payments and bookings.

For example, say you have $100 of credit and your deposit is $60. Your credit covers the whole deposit, so $0 is due today, and the other $40 goes toward your earliest payments.

On Defera Zero, nothing is due at booking, so your credit goes to your payments instead, starting with the earliest.

## Can I save some credit for another trip?

Yes. The "Defera Credit applied" panel lets you choose how much to use. For example, if you have $200 of credit and want to keep $50 for later, set it to use $150. Set it to $0, and the panel reads "Not using your Defera Credit on this trip", leaving your whole balance on your account.

## Why does checkout ask for my card?

Checkout asks for card details even when your credit covers the entire deposit. That isn't a demand for payment. Your payment plan needs a card on file, the card your later payments are charged to automatically, so checkout saves your card securely.

When your credit covers everything due today, your card is charged $0 at booking. The "Due today" line shows the real amount your card will be charged.

## What happens after I book?

- **Your confirmation email shows your credit.** It shows how much went to your deposit and how much is set aside for upcoming payments. Payments covered by credit say "Covered by credit", and the email shows the first payment your card is actually charged. If your credit covers every payment, it shows $0.00 due and says nothing is charged to your card.
- **Credit that arrives later doesn't apply on its own.** Credit you receive after booking won't reach a trip you already have by itself. To use it on that trip, open **Trips**, tap the trip, and tap **Apply Credits** in its payment schedule. It's spread across your upcoming payments, earliest first. Here's more on [using your Defera Credit](/help/changes-cancellations/use-defera-credit).

## Something look wrong?

A few things are easy to mix up:

- **Your trip cost more than your credit.** Your trip total includes all fees, so part of it may still be due.
- **The credit arrived after you booked.** It won't apply to that trip until you tap **Apply Credits**.
- **Miles aren't credit.** Defera Miles are separate, and you choose whether to apply them at checkout.

If your card was charged even though your credit covered what was due, or your credit balance looks wrong, contact us so our team can check your account. Chat with the [Defera Concierge](/support), our AI concierge, or send us a message from the [Contact page](/contact#message).`,
  },
  {
    slug: 'discount-codes',
    title: 'Using a discount code',
    summary: 'Not sure where a discount code goes or whether it worked? Ask the Defera Concierge before you book. Defera Credit and Miles don’t need a code.',
    keywords: ['promo code', 'coupon', 'coupon code', 'voucher', 'ecredit', 'savings', 'gift card'],
    related: ['booking/using-credit-at-checkout', 'rewards/using-miles-at-checkout', 'booking/checkout-page', 'getting-started/what-you-pay'],
    updated: '2026-10-23',
    body: `If you have a discount code and aren't sure where it goes or whether it applied, chat with the [Defera Concierge](/support), our AI concierge, before you book. It can send a request to our team, who can look at your checkout and give you a real answer.

In the meantime, it helps to know that some savings don't need a code at all. And whatever you apply, your checkout page shows your full price before you pay, so you can always check first.

## Which savings don't need a code?

- **Defera Credit** applies automatically at checkout, to your deposit first. There's no code to enter and nothing to switch on.
- **Defera Miles** appear like a payment method at checkout, right above where you add your card. Tap **Apply to trip** to use as many Miles as the trip allows, or **Choose an amount** to pick your own. You'll need at least {{MILES_CHECKOUT_MIN}} Miles to use them at checkout.

Here's more on [using your Miles at checkout](/help/rewards/using-miles-at-checkout).

## Where do airline credits, vouchers and gift cards go?

Apply these in **Travel Credits & Gift Cards** on your checkout page. You can add:

- an airline travel credit or eCredit
- a certificate or voucher
- an airline gift card

The amount comes off your fare right away, and your trip's price breakdown shows it as "Credits Applied". Each one shows "Pending verification" until our team confirms it with the airline.

These are airline credits, so they're separate from Defera Credit, which applies on its own.

## How can I check what I'll pay?

Whatever you apply, your checkout page shows the result before you pay:

- The **Price Breakdown** lists everything in your trip total.
- The "Due today" line shows exactly what your card will be charged now.

If something doesn't look right, ask us before you book.

## What should I send when I ask?

To help our team look into it, include:

- the code, exactly as you have it
- the trip you're booking: the route, dates and number of travelers
- what your checkout page shows, such as your trip total and what's due today

While you wait for an answer, keep in mind that fares can change until you book.

## How do I reach you?

- Chat with the [Defera Concierge](/support), our AI concierge.
- Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7.
- Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'booking-didnt-go-through',
    title: 'My booking didn’t go through. What now?',
    summary: 'First check Trips to see whether your booking went through. If it didn’t, check your card details and try again, and contact us if it still won’t work.',
    keywords: ['checkout error', 'payment failed', 'card declined', 'booking failed', 'error message', 'checkout not working', 'stuck at checkout'],
    related: ['payments/card-declined', 'payments/accepted-payment-methods', 'contact/how-to-contact', 'booking/checkout-page'],
    updated: '2026-10-23',
    body: `If you saw an error while booking, first check whether your booking went through anyway: refresh the page, then open **Trips**. If your trip isn't there, check your card details and try again. If checkout still won't go through, contact us so our team can take a look.

We know it's stressful when a booking doesn't go through, especially once you've found the flight you want. Here's how to work out what happened and what to do next.

## What should I do first?

Work through these in order:

1. **Refresh, then check Trips.** If your trip is in **Trips**, your booking went through, and you don't need to book it again. Your booking confirmation email, from bookings@mail.defera.app, should be on its way to your inbox too.
2. **Check your card details.** Make sure the card number, expiration date, CVC and ZIP code are entered correctly.
3. **Use a debit or credit card.** Those are the only payment methods we accept. Prepaid cards, including gift and stored-value cards, aren't accepted on payment plans, and the decline doesn't always say why. If you're using one, switch to a debit or credit card rather than trying the same card again.
4. **Still not working? Contact us.** Send us the details of the error, so our team can look at what happened in your checkout.

Hoping to pay from your bank account? We can't take payments straight from a bank account, but any debit card for that account works, including the one your bank already gave you. Here's the full list of [payment methods we accept](/help/payments/accepted-payment-methods).

## What should I include when I contact you?

- the exact wording of the error message you saw
- when it happened
- the trip you were booking: the route, dates and number of travelers
- the email on your Defera account

## Will the price be the same when I try again?

Not necessarily. Starting checkout doesn't hold a price, and fares can change until you book. So if you try again later, the price may be different. Your checkout page always shows the current trip total before you pay.

## What happens to Miles I applied?

They come back if the payment doesn't go through. Miles are set aside when you tap **Confirm**, before your card is charged, and they're returned if the payment fails.

There's one exception. If your bank asked you to approve the payment and that step wasn't finished, your Miles can stay set aside until our team releases them. If that happens, let us know.

## I see a charge but no trip. What now?

Contact us with the amount and the date the charge appeared, along with the details above, so our team can check what happened.

## How do I reach you?

- Chat with the [Defera Concierge](/support), our AI concierge. It can send a request to our team.
- Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7.
- Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'after-you-book',
    title: 'What happens after you book',
    summary: 'We email your booking confirmation, your trip appears in Trips and your payments run on their own. Your confirmation number comes once you’ve paid in full.',
    keywords: ['booking confirmation', 'next steps', 'confirmation email', 'booking reference', 'payment schedule', 'trip page', 'what now'],
    related: ['emails/booking-confirmation-email', 'confirmation-and-travel/when-confirmation-number', 'payments/view-payment-schedule', 'changes-cancellations/cancel-within-24-hours'],
    updated: '2026-10-05',
    body: `Right after you book, we email your booking confirmation, and your trip appears in **Trips**. If you're on a payment plan, your payments are then charged automatically on the schedule you chose. Your airline confirmation number is released once your trip is paid in full.

From here, most of it takes care of itself. Your trip price was secured when your booking was approved, and it stays protected as long as you keep up with your payment plan.

## What happens next?

Here's what happens, from booking to takeoff:

1. **We email your booking confirmation.** It arrives right away, with your booking reference and all the details of your trip.
2. **Your trip appears in Trips.** Tap it any time to see your flights and your payment schedule, or to make changes.
3. **Your payments run automatically.** Each one is charged to your card on file, weekly or bi-weekly, on its scheduled date.
4. **Your trip is paid in full.** After your final payment, our team releases your airline confirmation number, usually within 24 hours on weekdays. We email you when it's ready, and the airline emails you directly too.
5. **You travel.** You use your confirmation number to check in with the airline.

## What's in my booking confirmation email?

It comes from bookings@mail.defera.app the moment you book. The subject line starts with your booking reference, followed by "You're going to" and your destination city. Inside, you'll find:

- **Your Defera booking reference,** at the top. It's how we find your trip, but it isn't your airline confirmation number. A copy icon beside it opens a page where you can copy it in one tap.
- **Your flights and travelers.** A card for each flight, with times, airports, airline, flight number, aircraft and fare, then every traveler detail entered at checkout. Passport, ID and Known Traveler numbers show only their last 4 digits; the full numbers are on your trip's Flight Details screen.
- **How we approach seats:** window seats, companions together and as close to the front as possible, when the fare allows.
- **Your payment summary and a timeline,** from booking, through your payments and your confirmation number, to departure.
- **Credits applied,** such as Defera Credit, an airline travel credit or a gift card.
- **Add to calendar,** a button that adds every flight to your calendar at the right local times.
- **Answers to common questions** about cancelling, Defera Credit, changing your flight, paying early, adding seats and bags, and changing your schedule or card.

Can't find it? Everything in [your booking confirmation email](/help/emails/booking-confirmation-email) is also on your trip page.

## What can I do from my trip page?

Open **Trips** and tap your trip. You'll find:

- **Your payment schedule,** with **Make a Payment** for paying ahead or paying off early, and **Update Card** for changing the card your payments are charged to.
- **The Flight Options menu,** at the top right of the trip's Flight Details section, for changing your flight, adding seats or bags, or cancelling.
- **Your Trip Statement,** a printable PDF of your itinerary, travelers, pricing and payment plan. Tap the download icon in the trip's header row to get it.

## How do my payments work?

[Your payments](/help/payments/how-installments-work) are charged automatically to your card on file, the card saved for your trip, weekly or bi-weekly on the schedule you chose. You don't need to remember to pay. You can also pay ahead or pay off early at any time from **Make a Payment**, with no penalty and no interest.

## When do I get my confirmation number?

Your airline confirmation number is [released once your trip is paid in full](/help/confirmation-and-travel/when-confirmation-number), and it appears on your trip page. When it's ready, we send you the "Your trip has been paid off!" email, with a **Get Confirmation Number** button.

Until then, it's completely normal for your trip to have a payment schedule and no confirmation number yet. Paying early gets it to you sooner.

## Can I add seats or bags later?

Yes. You can add seats and checked bags at any time after booking, from **Flight Options**, then **Purchase Seats** or **Purchase Baggage**, whether your plan is still running or already paid in full.

## What if I change my mind?

If you [cancel within 24 hours of booking](/help/changes-cancellations/cancel-within-24-hours), your deposit is refunded in full to the card you paid with, automatically. After that, the cancellation options for your trip are shown when you tap **Cancel** in **Flight Options**.`,
  },
];
